'use client';

import { create } from 'zustand';

/**
 * The site locale ('nl' | 'fr' | 'en') the AI chat should ALWAYS reply in. The
 * route pins the reply language to the request body's `locale` (deterministic —
 * never inferred from the message), and most send paths set it explicitly. But
 * `regenerate`/edit go through the transport with no caller body, so without this
 * they'd fall back to the browser `Accept-Language` (which can differ from the
 * site → wrong-language reply). The chat hook keeps this in sync with the active
 * i18n locale; the transport injects it into every request body.
 */
type ChatLocale = 'nl' | 'fr' | 'en';

type ChatLocaleState = {
    locale: ChatLocale | null;
    setLocale: (locale: ChatLocale) => void;
};

const useStore = create<ChatLocaleState>(set => ({
    locale: null,
    setLocale: locale => set(state => (state.locale === locale ? state : { locale })),
}));

/** Non-reactive read for the chat transport when building the request body. */
export function getChatLocale(): ChatLocale | null {
    return useStore.getState().locale;
}

export function setChatLocale(locale: ChatLocale): void {
    useStore.getState().setLocale(locale);
}
